import React, {useEffect, useState} from 'react';
import {
    Alert,
    App,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    InputNumber,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography
} from 'antd';
import {useNavigate} from 'react-router-dom';
import {
    ClockCircleOutlined,
    DollarOutlined,
    FundOutlined,
    RobotOutlined,
    SecurityScanOutlined,
    SettingOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import {userAiConfigService} from './userAiConfigService';
import {agentConfigService} from '../../services/agentConfigService';
import {
    AiTradeMode,
    AiTradeModeMap,
    RiskMode,
    RiskModeMap,
    TradeType,
    TradeTypeMap,
    TradingStyle,
    TradingStyleMap,
    UserAiTradeConfig,
    VALID_FREQUENCIES
} from './types';

const {Title, Text} = Typography;

const UserAiConfig: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const {message} = App.useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<UserAiTradeConfig | null>(null);
    const [agents, setAgents] = useState<Array<{ id: number; name: string }>>([]);

    useEffect(() => {
        loadConfig();
        loadAgents();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const data = await userAiConfigService.getConfig();
            setConfig(data);
        } catch (error) {
            console.error('加载配置失败:', error);
            messageApi.error('加载配置失败');
        } finally {
            setLoading(false);
        }
    };

    const loadAgents = async () => {
        try {
            const data = await agentConfigService.getActiveAgentConfigs();
            setAgents(data.map((a: any) => ({id: a.id, name: a.name})));
        } catch (error) {
            console.error('加载Agent列表失败:', error);
        }
    };

    const handleAiTradeModeChange = async (mode: AiTradeMode) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateAiTradeMode(mode);
            setConfig(updated);
            messageApi.success('AI交易模式已更新');
        } catch (error) {
            console.error('更新AI交易模式失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleFrequencyChange = async (frequency: number) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateAiTradeFrequency(frequency);
            setConfig(updated);
            messageApi.success('频次已更新');
        } catch (error) {
            console.error('更新频次失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleRiskModeChange = async (mode: RiskMode) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateRiskMode(mode);
            setConfig(updated);
            messageApi.success('风控模式已更新');
        } catch (error) {
            console.error('更新风控模式失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleTradingStyleChange = async (style: TradingStyle) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateTradingStyle(style);
            setConfig(updated);
            messageApi.success('交易风格已更新');
        } catch (error) {
            console.error('更新交易风格失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleTradeTypeChange = async (tradeType: TradeType) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateTradeType(tradeType);
            setConfig(updated);
            messageApi.success('交易类型已更新');
        } catch (error) {
            console.error('更新交易类型失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleMaxParallelCapitalChange = async (value: number | null) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateMaxParallelCapital(value);
            setConfig(updated);
            messageApi.success('最大并行资金已更新');
        } catch (error) {
            console.error('更新最大并行资金失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    const handleAgentChange = async (agentId: number | null) => {
        if (!config || saving) return;
        setSaving(true);
        try {
            const updated = await userAiConfigService.updateAgentConfigId(agentId);
            setConfig(updated);
            messageApi.success('交易Agent已更新');
        } catch (error) {
            console.error('更新交易Agent失败:', error);
            messageApi.error('更新失败');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
                <Spin size="large" tip="加载配置中..."/>
            </div>
        );
    }

    if (!config) {
        return (
            <div style={{padding: '24px'}}>
                <Alert message="无法加载配置" type="error"/>
            </div>
        );
    }

    const currentStyle = TradingStyleMap[config.tradingStyle];

    return (
        <div style={{
            padding: '24px',
            background: 'var(--pt-bg-base)',
            minHeight: '100vh',
            color: 'var(--pt-text-primary)'
        }}>
            <Title level={3} style={{color: 'var(--pt-text-primary)', marginBottom: 24}}>
                <Space>
                    <RobotOutlined/>
                    AI交易配置
                </Space>
            </Title>

            <Row gutter={[24, 24]}>
                {/* AI交易模式 */}
                <Col xs={24} md={6}>
                    <Card
                        title={
                            <Space>
                                <ThunderboltOutlined/>
                                <span>AI交易模式</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="AI交易模式控制AI是否自动执行交易"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <Radio.Group
                                value={config.aiTradeMode}
                                onChange={(e) => handleAiTradeModeChange(e.target.value)}
                                disabled={saving}
                            >
                                <Space direction="vertical">
                                    <Radio value={AiTradeMode.MANUAL}>
                                        <Space>
                                            <span>⚙️ 手动</span>
                                            <Tag color="orange">推荐</Tag>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            AI仅提供建议，需要手动确认执行
                                        </div>
                                    </Radio>
                                    <Radio value={AiTradeMode.AUTO}>
                                        <Space>
                                            <span>🤖 自动</span>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            AI自动执行交易，无需手动确认
                                        </div>
                                    </Radio>
                                </Space>
                            </Radio.Group>

                            {config.aiTradeMode === AiTradeMode.AUTO && (
                                <>
                                    <Divider style={{margin: '12px 0', borderColor: 'var(--pt-border-subtle)'}}/>
                                    <Space direction="vertical" size="middle" style={{width: '100%'}}>
                                        <div>
                                            <Space>
                                                <ClockCircleOutlined/>
                                                <Text style={{color: 'var(--pt-text-primary)'}}>执行频次：</Text>
                                            </Space>
                                            <Select
                                                value={config.aiTradeFrequency}
                                                onChange={handleFrequencyChange}
                                                disabled={saving}
                                                style={{width: 120, marginLeft: 12}}
                                                options={VALID_FREQUENCIES.map(f => ({
                                                    value: f,
                                                    label: `${f} 分钟`
                                                }))}
                                            />
                                        </div>

                                        <div>
                                            <Space align="start"
                                                   style={{width: '100%', justifyContent: 'space-between'}}>
                                                <Space>
                                                    <RobotOutlined/>
                                                    <Text
                                                        style={{color: 'var(--pt-text-primary)'}}>AI交易Prompt设定：</Text>
                                                </Space>
                                                <Button
                                                    type="primary"
                                                    ghost
                                                    size="small"
                                                    onClick={() => navigate('/user-ai-prompt-config')}
                                                    disabled={saving}
                                                >
                                                    去设置
                                                </Button>
                                            </Space>
                                            <Text style={{
                                                display: 'block',
                                                marginTop: 8,
                                                color: 'var(--pt-text-secondary)',
                                                fontSize: 12
                                            }}>
                                                自动模式下可单独配置 AI 交易 Prompt，点击后进入专属设定页面。
                                            </Text>
                                        </div>
                                    </Space>
                                </>
                            )}
                        </Space>
                    </Card>
                </Col>

                {/* 风控模式 */}
                <Col xs={24} md={6}>
                    <Card
                        title={
                            <Space>
                                <SecurityScanOutlined/>
                                <span>风控模式</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="风控模式控制风险控制的方式"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <Radio.Group
                                value={config.riskMode}
                                onChange={(e) => handleRiskModeChange(e.target.value)}
                                disabled={saving}
                            >
                                <Space direction="vertical">
                                    <Radio value={RiskMode.MANUAL}>
                                        <Space>
                                            <span>⚙️ 手动模式</span>
                                            <Tag color="orange">人工审核 + AI建议</Tag>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            订单进入人工审核队列，审核弹窗可查看 AI 风控建议
                                        </div>
                                    </Radio>
                                    <Radio value={RiskMode.AUTO}>
                                        <Space>
                                            <span>🤖 自动模式</span>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            保持现有自动下单流程，不启用人工审核和 AI 风控拦截
                                        </div>
                                    </Radio>
                                    <Radio value={RiskMode.AI}>
                                        <Space>
                                            <span>🧠 AI模式</span>
                                            <Tag color="purple">自动AI风控</Tag>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-dim)', fontSize: 12}}>
                                            所有下单先经过 AI 风控，未通过时直接阻断并通知
                                        </div>
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Space>
                    </Card>
                </Col>

                {/* 交易类型 */}
                <Col xs={24} md={6}>
                    <Card
                        title={
                            <Space>
                                <DollarOutlined/>
                                <span>交易类型</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="交易类型决定使用模拟资金还是真实资金进行交易"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <Radio.Group
                                value={config.tradeType}
                                onChange={(e) => handleTradeTypeChange(e.target.value)}
                                disabled={saving}
                            >
                                <Space direction="vertical">
                                    <Radio value={TradeType.SIMULATION}>
                                        <Space>
                                            <span>🎮 模拟盘</span>
                                            <Tag color="blue">推荐新手</Tag>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            使用模拟资金交易，无真实资金风险
                                        </div>
                                    </Radio>
                                    <Radio value={TradeType.REAL}>
                                        <Space>
                                            <span>💰 实盘</span>
                                            <Tag color="orange">真实交易</Tag>
                                        </Space>
                                        <div style={{marginLeft: 24, color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                            使用真实资金交易，请做好风险管理
                                        </div>
                                    </Radio>
                                </Space>
                            </Radio.Group>

                            {config.tradeType === TradeType.REAL && (
                                <>
                                    <Divider style={{margin: '12px 0', borderColor: 'var(--pt-border-subtle)'}}/>
                                    <Alert
                                        message="实盘交易提醒"
                                        description="实盘交易将使用您的真实资金，请确保已充分了解交易风险，并做好资金管理。建议先在模拟盘中验证策略有效性。"
                                        type="warning"
                                        showIcon
                                        style={{background: 'var(--pt-bear-dim)', borderColor: 'var(--pt-bear)'}}
                                    />
                                </>
                            )}
                        </Space>
                    </Card>
                </Col>

                {/* 最大并行资金 */}
                <Col xs={24} md={6}>
                    <Card
                        title={
                            <Space>
                                <FundOutlined/>
                                <span>最大并行资金</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="设置AI交易可使用的最高保证金金额，留空则使用系统默认值"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <InputNumber
                                value={config.maxParallelCapital}
                                onChange={(value) => handleMaxParallelCapitalChange(value)}
                                disabled={saving}
                                min={0}
                                step={10}
                                precision={2}
                                style={{width: '100%'}}
                                placeholder="留空使用系统默认值"
                            />
                            <Text style={{color: 'var(--pt-text-secondary)', fontSize: 12}}>USDT</Text>
                        </Space>
                    </Card>
                </Col>

                {/* 交易Agent */}
                <Col xs={24} md={6}>
                    <Card
                        title={
                            <Space>
                                <RobotOutlined/>
                                <span>交易Agent</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="选择执行交易决策的智能体，留空使用系统默认AI_TRADING_AGENT"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />
                            <Select
                                value={config.agentConfigId ?? undefined}
                                onChange={(value) => handleAgentChange(value ?? null)}
                                disabled={saving}
                                allowClear
                                placeholder="使用系统默认Agent"
                                style={{width: '100%'}}
                                options={agents.map(a => ({
                                    value: a.id,
                                    label: a.name
                                }))}
                            />
                        </Space>
                    </Card>
                </Col>

                {/* 交易风格 */}
                <Col xs={24}>
                    <Card
                        title={
                            <Space>
                                <SettingOutlined/>
                                <span>交易风格</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="交易风格决定您的风险偏好和收益目标"
                                type="info"
                                showIcon
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <Radio.Group
                                value={config.tradingStyle}
                                onChange={(e) => handleTradingStyleChange(e.target.value)}
                                disabled={saving}
                                style={{width: '100%'}}
                            >
                                <Row gutter={[16, 16]}>
                                    {Object.values(TradingStyle).map(style => {
                                        const info = TradingStyleMap[style];
                                        return (
                                            <Col xs={24} sm={12} lg={8} key={style}>
                                                <Radio value={style} style={{width: '100%'}}>
                                                    <Card
                                                        size="small"
                                                        style={{
                                                            background: config.tradingStyle === style
                                                                ? 'var(--pt-primary-dim)'
                                                                : 'var(--pt-bg-card)',
                                                            borderColor: config.tradingStyle === style
                                                                ? 'var(--pt-primary)'
                                                                : 'var(--pt-border)',
                                                            cursor: 'pointer',
                                                            borderRadius: 'var(--pt-radius-sm)'
                                                        }}
                                                        styles={{body: {padding: '12px'}}}
                                                    >
                                                        <Space direction="vertical" size={4}>
                                                            <Space>
                                                                <span style={{fontSize: 18}}>{info.emoji}</span>
                                                                <Text strong style={{color: 'var(--pt-text-primary)'}}>
                                                                    {info.label}({style.split('_')[0]})
                                                                </Text>
                                                            </Space>
                                                            <Text style={{
                                                                fontSize: 12,
                                                                color: 'var(--pt-text-secondary)'
                                                            }}>
                                                                {info.description}
                                                            </Text>
                                                            <Space size={8}>
                                                                <Tag
                                                                    color="red">最大亏损 {Math.round(info.maxLossRate * 100)}%</Tag>
                                                                <Tag
                                                                    color="green">目标盈利 {Math.round(info.targetProfitRate * 100)}%</Tag>
                                                            </Space>
                                                        </Space>
                                                    </Card>
                                                </Radio>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Radio.Group>
                        </Space>
                    </Card>
                </Col>

                {/* 当前配置摘要 */}
                <Col xs={24}>
                    <Card
                        title="当前配置"
                        size="small"
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Descriptions column={{xs: 1, sm: 2, lg: 4}} size="small">
                            <Descriptions.Item label="AI交易模式">
                                <Tag color={config.aiTradeMode === AiTradeMode.AUTO ? 'green' : 'orange'}>
                                    {AiTradeModeMap[config.aiTradeMode]}
                                </Tag>
                                {config.aiTradeMode === AiTradeMode.AUTO && (
                                    <Tag color="blue">{config.aiTradeFrequency}分钟/次</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="风控模式">
                                <Tag color={config.riskMode === RiskMode.AUTO ? 'green' : 'orange'}>
                                    {RiskModeMap[config.riskMode]}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="交易类型">
                                <Tag color={config.tradeType === TradeType.REAL ? 'orange' : 'blue'}>
                                    {TradeTypeMap[config.tradeType].emoji} {TradeTypeMap[config.tradeType].label}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="交易风格">
                                <Tag color="blue">
                                    {currentStyle.emoji} {currentStyle.label}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="交易Agent">
                                {config.agentConfigId != null ? (
                                    <Tag
                                        color="purple">{agents.find(a => a.id === config.agentConfigId)?.name || `ID:${config.agentConfigId}`}</Tag>
                                ) : (
                                    <Tag color="default">系统默认 (AI_TRADING_AGENT)</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="最大并行资金">
                                {config.maxParallelCapital != null ? (
                                    <Tag color="green">{config.maxParallelCapital} USDT</Tag>
                                ) : (
                                    <Tag color="default">系统默认</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="更新时间">
                                {new Date(config.updateTime).toLocaleString('zh-CN')}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default UserAiConfig;
